import { useState } from 'react';
import { IoWarningOutline } from "react-icons/io5";
import { MdOutlineCancel } from "react-icons/md";
import { PiTrashFill } from 'react-icons/pi';
import { ActionIcon, Button, Text, Title } from 'rizzui';
import cn from '../../utils/classNames';

type CancelPopupProps = {
    title: string;
    description: string;
    onDelete: (reason?: string) => void; // ahora recibe razón opcional
    size?: string | number;
    className?: string;
    actionIconClassName?: string;
    Icon?: React.ReactNode;
    noBorder?: boolean;
};

export default function CancelPopup({
                                        title,
                                        description,
                                        onDelete,
                                        size = 20,
                                        className,
                                        actionIconClassName,
                                        Icon,
                                        noBorder = false,
                                    }: CancelPopupProps) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState<string>('');

    const close = () => {
        setOpen(false);
        setReason('');
    };

    const confirm = () => {
        onDelete(reason?.trim() ? reason.trim() : undefined);
        close();
    };

    return (
        <>
            {/* Trigger */}
            <ActionIcon
                size="sm"
                variant={noBorder ? 'solid' : 'outline'}
                aria-label="Cancel Item"
                onClick={() => setOpen(true)}
                className={cn(
                    [actionIconClassName, `cursor-pointer ${noBorder ? '' : 'hover:!border-gray-900 hover:text-gray-700'} bg-red`],
                    className
                )}
            >
                {Icon ?? <MdOutlineCancel className={noBorder ? 'h-6 w-6' : 'h-4 w-4'} color="white" />}
            </ActionIcon>

            {/* Modal */}
            {open && (
                <div
                    className="fixed inset-0 z-[1000] flex items-center justify-center"
                    aria-modal="true"
                    role="dialog"
                >
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={close}
                    />

                    {/* Dialog */}
                    <div className="relative z-[1001] w-full max-w-md rounded-xl bg-white p-4 shadow-xl">
                        <div className="mb-2 flex items-center justify-center">
                            <Title as="h6" className="mb-0.5 flex items-center text-sm text-gray-700">
                                <PiTrashFill className="me-1 h-[17px] w-[17px]" />
                                {title}
                            </Title>
                        </div>

                        <div className="mb-2 flex justify-center">
                            <IoWarningOutline className="text-yellow-500" size={30} />
                        </div>

                        <Text className="mb-3 leading-relaxed text-center text-gray-600">
                            {description}
                        </Text>

                        {/* Textarea de razón (opcional) */}
                        <div className="mb-4">
                            <label htmlFor="cancel-reason" className="mb-1 block text-xs font-medium text-gray-600">
                                Motivo de cancelación (opcional)
                            </label>
                            <textarea
                                id="cancel-reason"
                                rows={3}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Escribe el motivo si lo necesitas…"
                                className="w-full resize-y rounded-md border border-gray-200 p-2 text-sm outline-none focus:border-gray-400"
                            />
                        </div>

                        <div className="flex items-center justify-center gap-3">
                            <Button size="sm" variant="outline" className="h-7" onClick={close}>
                                No
                            </Button>
                            <Button size="sm" color="danger" className="h-7" onClick={confirm}>
                                Sí
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
